/**
 * 추출힌트/검토플래그 자가개선 제안 페이지 (M23) — admin 전용.
 *
 * - "제안 생성" 버튼: 검수 정정(Record.edits) 집계 → 실제 문서 이미지 기반 vision LLM 초안 → pending 저장.
 * - 대기중(pending) 제안 목록: 두 종류(kind) + 근거(빈도/비율/예시 before→after) + [적용]/[반려].
 *   - extraction_hint(위치·구조 힌트) → agent.extraction_rules 병합.
 *   - review_flag(검토 필요/상습오류) → agent.review_rules 병합, 최종 AI 검토에 soft 주입(강제 아님).
 *   - 제안 문구는 승인 전 관리자가 직접 편집 가능(편집값이 그대로 반영/AuditLog 기록).
 *   - 예시(record_id)의 원본 PDF 페이지를 우측 패널에서 확인하며 판단.
 *   [적용] → 기존 update_agent 재사용, AuditLog 남김.
 * - 하단 분석 리포트: 대리인×필드 정정 통계(임계값 완화).
 */

import { useEffect, useState } from "react";
import {
  AlertTriangle,
  Check,
  ChevronLeft,
  ChevronRight,
  Eye,
  FileText,
  FlaskConical,
  Loader2,
  RefreshCw,
  Sparkles,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { ApiError } from "@/lib/api/client";
import type { RuleSuggestionBacktest, RuleSuggestionOut } from "@/lib/api/ruleSuggestions";
import { cn } from "@/lib/cn";

import {
  useApproveSuggestion,
  useBacktestSuggestion,
  useFieldErrors,
  useGenerateSuggestions,
  useRejectSuggestion,
  useSuggestionRecordMeta,
  useSuggestionRecordPageImage,
  useSuggestions,
} from "./useRuleSuggestions";

/** 우측 패널이 표시할 예시 원본 페이지 타깃. */
export interface PreviewTarget {
  recordId: string;
  page: number;
  label: string;
}

function pct(rate: number): string {
  return `${Math.round(rate * 100)}%`;
}

function KindBadge({ kind }: { kind: RuleSuggestionOut["kind"] }) {
  const { t } = useTranslation();
  const isFlag = kind === "review_flag";
  return (
    <span
      className={cn(
        "rounded-full px-2 py-0.5 text-[11px] font-medium",
        isFlag ? "bg-amber-50 text-amber-700" : "bg-sky-50 text-sky-700",
      )}
    >
      {isFlag ? t("admin.suggestions.kind.reviewFlag") : t("admin.suggestions.kind.extractionHint")}
    </span>
  );
}

function SuggestionCard({
  item,
  onPreview,
  activeRecordId,
}: {
  item: RuleSuggestionOut;
  onPreview: (target: PreviewTarget) => void;
  activeRecordId: string | null;
}) {
  const { t } = useTranslation();
  const approve = useApproveSuggestion();
  const reject = useRejectSuggestion();
  const backtest = useBacktestSuggestion();
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState(item.proposed_hint);
  const [backtestResult, setBacktestResult] = useState<RuleSuggestionBacktest | null>(null);

  const busy = approve.isPending || reject.isPending || backtest.isPending;
  const ev = item.evidence ?? {};
  const examples = ev.examples ?? [];
  const isFlag = item.kind === "review_flag";
  const trimmed = draft.trim();
  const dirty = trimmed !== item.proposed_hint.trim();

  const handleBacktest = async () => {
    setError(null);
    setBacktestResult(null);
    try {
      const res = await backtest.mutateAsync({
        id: item.id,
        editedHint: trimmed || undefined,
      });
      setBacktestResult(res);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t("admin.suggestions.errors.generic"));
    }
  };

  const handleApprove = async () => {
    setError(null);
    if (!trimmed) {
      setError(t("admin.suggestions.errors.emptyHint"));
      return;
    }
    try {
      await approve.mutateAsync({ id: item.id, editedHint: trimmed });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t("admin.suggestions.errors.generic"));
    }
  };
  const handleReject = async () => {
    setError(null);
    try {
      await reject.mutateAsync(item.id);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t("admin.suggestions.errors.generic"));
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded bg-slate-100 px-2 py-0.5 font-mono text-xs text-slate-600">
              {item.agent_id}
            </span>
            <span className="truncate text-sm font-medium text-slate-800">
              {item.official_name ?? "—"}
            </span>
            <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-700">
              {item.field}
            </span>
            <KindBadge kind={item.kind} />
          </div>
          {typeof ev.count === "number" && typeof ev.total === "number" && (
            <p className="mt-1 text-xs text-slate-500">
              {t("admin.suggestions.evidenceLine", {
                count: ev.count,
                total: ev.total,
                rate: pct(ev.rate ?? 0),
              })}
            </p>
          )}
        </div>
        <div className="flex shrink-0 gap-2">
          {!isFlag && (
            <button
              type="button"
              onClick={handleBacktest}
              disabled={busy}
              title={t("admin.suggestions.backtest.hint")}
              className="inline-flex items-center gap-1 rounded-md border border-indigo-300 px-3 py-1.5 text-sm font-medium text-indigo-600 hover:bg-indigo-50 disabled:opacity-50"
            >
              {backtest.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <FlaskConical className="h-4 w-4" />
              )}
              {t("admin.suggestions.backtest.run")}
            </button>
          )}
          <button
            type="button"
            onClick={handleApprove}
            disabled={busy}
            className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {approve.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Check className="h-4 w-4" />
            )}
            {t("admin.suggestions.approve")}
          </button>
          <button
            type="button"
            onClick={handleReject}
            disabled={busy}
            className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          >
            <X className="h-4 w-4" />
            {t("admin.suggestions.reject")}
          </button>
        </div>
      </div>

      <div className={cn("mt-3 rounded-md p-3", isFlag ? "bg-amber-50" : "bg-slate-50")}>
        <div className="flex items-center justify-between gap-2">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            {isFlag ? t("admin.suggestions.reviewNote") : t("admin.suggestions.proposedHint")}
          </p>
          {dirty && (
            <span className="rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700">
              {t("admin.suggestions.edited")}
            </span>
          )}
        </div>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          disabled={busy}
          rows={Math.min(6, Math.max(2, draft.split("\n").length))}
          className={cn(
            "mt-1 w-full resize-y rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-800 focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-300 disabled:opacity-60",
            isFlag ? "" : "font-mono",
          )}
        />
        {dirty && (
          <button
            type="button"
            onClick={() => setDraft(item.proposed_hint)}
            disabled={busy}
            className="mt-1 text-[11px] text-slate-400 underline hover:text-slate-600 disabled:opacity-50"
          >
            {t("admin.suggestions.resetEdit")}
          </button>
        )}
        {!isFlag && ev.current_hint ? (
          <p className="mt-2 text-xs text-slate-400">
            {t("admin.suggestions.currentHint")}:{" "}
            <span className="font-mono">{ev.current_hint}</span>
          </p>
        ) : null}
        {ev.reason ? <p className="mt-2 text-xs text-slate-500">💡 {ev.reason}</p> : null}
      </div>

      {examples.length > 0 && (
        <div className="mt-3">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            {t("admin.suggestions.examples")}
          </p>
          <ul className="mt-1 space-y-1">
            {examples.map((ex, idx) => {
              const label = `${item.field} · ${ex.before || "∅"} → ${ex.after || "∅"}`;
              const isActive = !!ex.record_id && ex.record_id === activeRecordId;
              return (
                <li key={idx} className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="rounded bg-rose-50 px-1.5 py-0.5 font-mono text-rose-600 line-through">
                    {ex.before || "∅"}
                  </span>
                  <span className="text-slate-400">→</span>
                  <span className="rounded bg-emerald-50 px-1.5 py-0.5 font-mono text-emerald-700">
                    {ex.after || "∅"}
                  </span>
                  {ex.record_id && (
                    <button
                      type="button"
                      onClick={() =>
                        onPreview({
                          recordId: ex.record_id as string,
                          page: ex.page && ex.page >= 1 ? ex.page : 1,
                          label,
                        })
                      }
                      className={cn(
                        "inline-flex items-center gap-1 rounded border px-1.5 py-0.5 font-medium transition",
                        isActive
                          ? "border-indigo-400 bg-indigo-50 text-indigo-700"
                          : "border-slate-200 text-slate-500 hover:border-indigo-300 hover:text-indigo-600",
                      )}
                    >
                      <Eye className="h-3 w-3" />
                      {t("admin.suggestions.viewSource")}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {backtest.isPending && (
        <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-indigo-600">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          {t("admin.suggestions.backtest.running")}
        </p>
      )}

      {backtestResult && !backtest.isPending && (
        <div className="mt-3 rounded-md border border-indigo-100 bg-indigo-50/40 p-3">
          {backtestResult.applicable ? (
            <>
              <div className="flex items-center justify-between gap-2">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  {t("admin.suggestions.backtest.title")}
                </p>
                <span
                  className={cn(
                    "rounded-full px-2 py-0.5 text-[11px] font-semibold",
                    backtestResult.total > 0 && backtestResult.pass_count === backtestResult.total
                      ? "bg-emerald-100 text-emerald-700"
                      : backtestResult.pass_count === 0
                        ? "bg-rose-100 text-rose-700"
                        : "bg-amber-100 text-amber-700",
                  )}
                >
                  {t("admin.suggestions.backtest.summary", {
                    pass: backtestResult.pass_count,
                    total: backtestResult.total,
                  })}
                </span>
              </div>
              <ul className="mt-2 space-y-1.5">
                {backtestResult.results.map((r, idx) => (
                  <li key={idx} className="flex flex-wrap items-center gap-2 text-xs">
                    {r.error ? (
                      <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-500" />
                    ) : r.match ? (
                      <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
                    ) : (
                      <X className="h-3.5 w-3.5 shrink-0 text-rose-500" />
                    )}
                    {r.error ? (
                      <span className="text-amber-600">{r.error}</span>
                    ) : (
                      <>
                        <span className="text-slate-400">
                          {t("admin.suggestions.backtest.expected")}:
                        </span>
                        <span className="rounded bg-emerald-50 px-1.5 py-0.5 font-mono text-emerald-700">
                          {r.expected || "∅"}
                        </span>
                        <span className="text-slate-400">
                          {t("admin.suggestions.backtest.got")}:
                        </span>
                        <span
                          className={cn(
                            "rounded px-1.5 py-0.5 font-mono",
                            r.match ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-600",
                          )}
                        >
                          {r.extracted || "∅"}
                        </span>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="text-xs text-slate-500">
              {backtestResult.message ?? t("admin.suggestions.backtest.notApplicable")}
            </p>
          )}
        </div>
      )}

      {error && <p className="mt-2 text-sm text-rose-600">{error}</p>}
    </div>
  );
}

/** 우측 고정 패널 — 선택한 예시 레코드의 원본 PDF 페이지를 렌더. */
function SuggestionPdfPanel({ target, onClose }: { target: PreviewTarget; onClose: () => void }) {
  const { t } = useTranslation();
  const meta = useSuggestionRecordMeta(target.recordId);
  const [page, setPage] = useState(target.page);
  useEffect(() => {
    setPage(target.page);
  }, [target.recordId, target.page]);

  const pageCount = meta.data?.page_count ?? 1;
  const img = useSuggestionRecordPageImage(target.recordId, page);

  return (
    <div className="flex max-h-[calc(100vh-2rem)] flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center justify-between gap-2 border-b border-slate-200 px-3 py-2">
        <div className="flex min-w-0 items-center gap-2">
          <FileText className="h-4 w-4 shrink-0 text-indigo-500" />
          <span className="truncate text-xs font-medium text-slate-700" title={target.label}>
            {target.label}
          </span>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          aria-label={t("admin.suggestions.closePreview")}
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="flex items-center justify-center gap-3 border-b border-slate-100 px-3 py-1.5 text-xs text-slate-500">
        <button
          type="button"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page <= 1}
          className="rounded p-1 hover:bg-slate-100 disabled:opacity-30"
          aria-label="prev"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="tabular-nums">
          {page} / {pageCount}
        </span>
        <button
          type="button"
          onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
          disabled={page >= pageCount}
          className="rounded p-1 hover:bg-slate-100 disabled:opacity-30"
          aria-label="next"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      <div className="flex-1 overflow-auto bg-slate-100 p-2">
        {img.isLoading ? (
          <div className="flex h-40 items-center justify-center text-slate-400">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : img.error ? (
          <p className="p-4 text-center text-xs text-rose-500">
            {t("admin.suggestions.previewError")}
          </p>
        ) : img.url ? (
          <img
            src={img.url}
            alt={target.label}
            className="mx-auto w-full rounded border border-slate-200 bg-white shadow-sm"
          />
        ) : null}
      </div>
    </div>
  );
}

export default function RuleSuggestionsPage() {
  const { t } = useTranslation();
  const pending = useSuggestions("pending");
  const generate = useGenerateSuggestions();
  const [showReport, setShowReport] = useState(false);
  const fieldErrors = useFieldErrors({ minCount: 1, minRate: 0, enabled: showReport });
  const [genMsg, setGenMsg] = useState<string | null>(null);
  const [genErr, setGenErr] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewTarget | null>(null);

  const handleGenerate = async () => {
    setGenMsg(null);
    setGenErr(null);
    try {
      const res = await generate.mutateAsync();
      setGenMsg(
        t("admin.suggestions.generateResult", {
          created: res.created,
          updated: res.updated,
          skipped: res.skipped,
          extractionHints: res.extraction_hints,
          reviewFlags: res.review_flags,
        }),
      );
    } catch (e) {
      setGenErr(e instanceof ApiError ? e.message : t("admin.suggestions.errors.generic"));
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <div className="flex gap-6">
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="flex items-center gap-2 text-xl font-semibold text-slate-800">
                <Sparkles className="h-5 w-5 text-indigo-500" />
                {t("admin.suggestions.title")}
              </h1>
              <p className="mt-1 text-sm text-slate-500">{t("admin.suggestions.description")}</p>
            </div>
            <button
              type="button"
              onClick={handleGenerate}
              disabled={generate.isPending}
              className="inline-flex shrink-0 items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {generate.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              {t("admin.suggestions.generate")}
            </button>
          </div>

          {genMsg && (
            <div className="mt-3 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
              {genMsg}
            </div>
          )}
          {genErr && (
            <div className="mt-3 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {genErr}
            </div>
          )}

          <div className="mt-6">
            <h2 className="text-sm font-semibold text-slate-700">
              {t("admin.suggestions.pendingTitle")}{" "}
              {pending.data ? (
                <span className="text-slate-400">({pending.data.length})</span>
              ) : null}
            </h2>
            {pending.isLoading ? (
              <div className="mt-4 flex justify-center py-8 text-slate-400">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : pending.data && pending.data.length > 0 ? (
              <div className="mt-3 space-y-3">
                {pending.data.map((item) => (
                  <SuggestionCard
                    key={item.id}
                    item={item}
                    onPreview={setPreview}
                    activeRecordId={preview?.recordId ?? null}
                  />
                ))}
              </div>
            ) : (
              <p className="mt-4 rounded-md border border-dashed border-slate-200 py-8 text-center text-sm text-slate-400">
                {t("admin.suggestions.empty")}
              </p>
            )}
          </div>

          <div className="mt-8 border-t border-slate-200 pt-4">
            <button
              type="button"
              onClick={() => setShowReport((v) => !v)}
              className="flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-800"
            >
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              {t("admin.suggestions.reportToggle")}
            </button>
            {showReport && (
              <div className="mt-3 overflow-x-auto rounded-lg border border-slate-200">
                {fieldErrors.isLoading ? (
                  <div className="flex justify-center py-8 text-slate-400">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                ) : (
                  <table className="w-full text-left text-sm">
                    <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                      <tr>
                        <th className="px-3 py-2">{t("admin.suggestions.table.agent")}</th>
                        <th className="px-3 py-2">{t("admin.suggestions.table.field")}</th>
                        <th className="px-3 py-2 text-right">
                          {t("admin.suggestions.table.count")}
                        </th>
                        <th className="px-3 py-2 text-right">
                          {t("admin.suggestions.table.rate")}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {(fieldErrors.data ?? []).map((row) => (
                        <tr key={`${row.agent_id}-${row.field}`} className="hover:bg-slate-50">
                          <td className="px-3 py-2">
                            <span className="font-mono text-xs text-slate-500">{row.agent_id}</span>{" "}
                            {row.official_name ?? ""}
                          </td>
                          <td className="px-3 py-2">{row.field}</td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {row.count}/{row.total}
                          </td>
                          <td
                            className={cn(
                              "px-3 py-2 text-right tabular-nums",
                              row.rate >= 0.3 ? "font-semibold text-rose-600" : "text-slate-500",
                            )}
                          >
                            {pct(row.rate)}
                          </td>
                        </tr>
                      ))}
                      {(fieldErrors.data ?? []).length === 0 && (
                        <tr>
                          <td colSpan={4} className="px-3 py-6 text-center text-slate-400">
                            {t("admin.suggestions.empty")}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        </div>

        {preview && (
          <aside className="hidden w-[420px] shrink-0 lg:block xl:w-[480px]">
            <div className="sticky top-4">
              <SuggestionPdfPanel target={preview} onClose={() => setPreview(null)} />
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}
